import React, { useCallback } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { CloseOutline, SyncOutline } from '@lyearn/icons';
import { EditorView } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import {
  Button,
  DeprecatedIconButton,
  IconButton,
  LinkButton,
  Stack,
  Tooltip,
  Typography,
} from '@lyearn/ui';
import useAnalytics from '@/components/Analytics/hooks/useAnalytics';
import AIDisclaimer from '@/components/LyearnAI/components/AIDisclaimer';
import { suggestionsContainerStyles } from '@/components/LyearnAI/components/SuggestionsContainer/styles';
import Typer from '@/components/LyearnAI/components/Typer';
import AIFeedback from '@/features/ai-feedback';

import useQuestionRephrase from '../../hooks/useQuestionRephrase';

function QuestionSuggestionContainer({
  editorView,
  onClose,
}: {
  editorView: EditorView;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const {
    suggestion,
    fetching,
    typing,
    generativeTaskId,
    replaceQuestion,
    regenerateQuestion,
    stopGnerationCallback,
    isStopGenerating,
  } = useQuestionRephrase({ editorView, onClose });
  const { track } = useAnalytics();

  const _regenerateQuestion = useCallback(() => {
    track('Regenerate rephrase question', { context: suggestion?.text });
    regenerateQuestion();
  }, [regenerateQuestion, suggestion?.text, track]);

  const _stopGnerationCallback = useCallback(() => {
    track('Stop generating rephrase question');
    stopGnerationCallback();
  }, [stopGnerationCallback, track]);

  return (
    <Stack
      className={cn(
        suggestionsContainerStyles,
        'mt-12 w-full rounded-12 border border-dashed border-bg-primary shadow-02',
      )}
      direction="col">
      <Stack className="mb-20 pt-12 pb-8 pe-12 ps-16" justifyContent="between">
        <Typography
          className="mt-4 inline max-w-[46.4rem] whitespace-pre-wrap"
          color="text-text-primary"
          variant="body-short-01">
          {suggestion?.text}
          {typing || fetching ? <Typer /> : null}
        </Typography>
        <IconButton
          Icon={CloseOutline}
          appearance="secondary"
          shape="square"
          size="s"
          variant="ghost"
          onClick={onClose}
        />
      </Stack>
      {generativeTaskId && !typing ? (
        <Stack className="mb-16 px-16" onClick={stopEventPropagation}>
          <AIFeedback entityId={generativeTaskId} />
        </Stack>
      ) : null}
      <Stack
        alignItems="center"
        className="h-56 flex-shrink-0 border-t border-border pe-12 ps-16"
        justifyContent="between">
        {typing ? (
          <LinkButton
            className="py-2 px-4"
            disabled={isStopGenerating}
            onClick={_stopGnerationCallback}>
            {t('QuizCreator:AI.StopGenerating')}
          </LinkButton>
        ) : (
          <AIDisclaimer variant="tooltip" />
        )}
        <Stack gapX={12}>
          <Tooltip placement="top" title={t('QuizCreator:AI.Regenerate')}>
            <DeprecatedIconButton
              appearance="secondary"
              disabled={typing}
              shape="square"
              size="x-small"
              variant="outlined"
              onClick={_regenerateQuestion}>
              <SyncOutline fillColorToken="icon-secondary" />
            </DeprecatedIconButton>
          </Tooltip>
          <Stack className={cn({ 'cursor-not-allowed': typing || fetching })}>
            <Button
              className={cn({ 'pointer-events-none': typing || fetching })}
              shape="square"
              size="x-small"
              variant="filled"
              onClick={replaceQuestion}>
              {t('QuizCreator:AI.ReplaceQuestion')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default QuestionSuggestionContainer;
